import moment from 'moment'
import { FC, ReactNode, ReactElement, useEffect, useState } from 'react'
import classNames from 'classnames'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter'
import { numberWithCommas } from 'src/utils/numberFormat'
import { ReactComponent as CommentsIcon } from 'src/assets/svg/comments.svg'
import { ReactComponent as ExternalLinkSvg } from 'src/assets/svg/external-link.svg'
import { ReactComponent as PlanetIcon } from 'src/assets/svg/planet.svg'
import { getThreadExpandedText } from 'src/utils/getThreadExpandedText'
import { getThreadPreviewText } from 'src/utils/getThreadPreviewText'
import { DEFAULT_ORIGINAL_LANG_CODE } from 'src/constants/defaultOriginalLangCode'
import en from 'src/constants/en'
import { getMentionByline } from 'src/utils/mentionByline'
import { Type } from './enums/type.enum'
import styles from './liveNotificationItem.module.scss'
import { ClampedParagraph } from '../ClampedParagraph'
import HighlightedText from '../HighlightedText'

interface LiveNotificationItemProps {
  authorName?: string | null
  campaign?: string
  // Comments collected for the mentioned post, shown next to "View Details".
  commentsCount?: number
  handleCheckboxChange?: (id: string | undefined, isChecked: boolean) => void
  hideCheckboxOnHover?: boolean
  id?: string
  isChecked?: boolean
  keyword?: string
  language?: string
  link?: string
  // Renders "View Details" on post-type mentions. The campaign page passes it
  // only for posts that have comments; absent, nothing changes.
  onViewDetails?: () => void
  originalLangCode?: string
  originalText?: string
  score?: number
  sentiments?: string[]
  socialMediaMentionType?: string | null
  source?: string
  // News and websites: the outlet ("BMG"), shown instead of an author.
  sourceName?: string | null
  time?: string
  translatedText?: string
  type?: Type
}

export const LiveNotificationItem: FC<LiveNotificationItemProps> = ({
  link,
  source,
  time,
  campaign,
  originalText,
  keyword,
  sentiments = [],
  type = Type.OTHER,
  language,
  originalLangCode,
  translatedText,
  score,
  handleCheckboxChange,
  id,
  isChecked,
  hideCheckboxOnHover,
  socialMediaMentionType,
  onViewDetails,
  commentsCount,
  authorName,
  sourceName,
}) => {
  const icon: ReactNode = source ? SOCIAL_ICONS[source.toLowerCase()] : null
  const byline = getMentionByline({ authorName, sourceName })

  const [isTranslated, setIsTranslated] = useState(false)

  const [isExpanded, setIsExpanded] = useState(false)

  const [previewText, setPreviewText] = useState('')

  const handleChange = (isChecked: boolean) => {
    handleCheckboxChange?.(id, isChecked)
  }

  const [highlightedPreviewElement, setHighlightedPreviewElement] =
    useState<ReactElement | null>(null)

  const [highlightedExpandedElement, setHighlightedExpandedElement] =
    useState<ReactElement | null>(null)

  const text = isTranslated ? translatedText : originalText

  const getHighlightedText = (
    textToHighlight?: string
  ): ReactElement | null => (
    <HighlightedText
      keyword={keyword}
      sentiments={sentiments}
      textToHighlight={textToHighlight}
      type={type}
    />
  )

  useEffect(() => {
    const previewText = getThreadPreviewText({ keyword, text })
    const expandedText = getThreadExpandedText({ keyword, sentiments, text })

    setPreviewText(previewText)

    setHighlightedPreviewElement(getHighlightedText(previewText))

    setHighlightedExpandedElement(getHighlightedText(expandedText))
  }, [text, keyword])

  const languageLabel = language
    ? capitalizeFirstLetter(language)
    : originalLangCode

  const shouldShowTranslation =
    languageLabel && originalLangCode !== DEFAULT_ORIGINAL_LANG_CODE

  const showViewDetails =
    !!onViewDetails && socialMediaMentionType?.toLowerCase() !== 'comment'

  return (
    <div
      className={classNames(styles.liveNotificationItem, {
        [styles.checked]: isChecked,
        [styles.noHoverCheckbox]: hideCheckboxOnHover,
      })}
    >
      {/* Only the checkbox selects: clicking the mention to read, expand or
          open it must not tick it. */}
      <div className={styles.checkboxWrapper}>
        <input
          checked={isChecked}
          className={styles.checkbox}
          onChange={() => handleChange(!isChecked)}
          type="checkbox"
        />
      </div>
      <div className={styles.content}>
        <div className={styles.titleWrapper}>
          {source && (
            <div className={styles.title}>
              <div className={styles.icon}>{icon}</div>
              {capitalizeFirstLetter(source)}
              {byline && (
                <span className={styles.author} title={byline}>
                  · {byline}
                </span>
              )}
              {socialMediaMentionType?.toLowerCase() === 'comment' && (
                <div className={styles.commentBadge}>{en.comment}</div>
              )}
              {link && (
                <a
                  href={link}
                  onClick={(event) => event.stopPropagation()}
                  rel="noreferrer"
                  target="_blank"
                >
                  <ExternalLinkSvg className={styles.externalIcon} />
                </a>
              )}
              {source === 'Booking' && (
                <div className={styles.score}>{score}.0</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.campaignTitle}>
        {campaign} • {time && moment(time).fromNow()}
        {shouldShowTranslation && (
          <div className={styles.languageLabel}>
            <PlanetIcon />
            {languageLabel}
          </div>
        )}
      </div>

      {text && (
        <div className={styles.text}>
          {text === previewText ? (
            highlightedPreviewElement
          ) : (
            <ClampedParagraph
              isExpanded={isExpanded}
              switchExpandedState={() =>
                setIsExpanded((prevIsExpanded) => !prevIsExpanded)
              }
            >
              {isExpanded
                ? highlightedExpandedElement
                : highlightedPreviewElement}
            </ClampedParagraph>
          )}

          <div className={styles.translations}>
            {shouldShowTranslation && (
              <div
                className={styles.translate}
                onClick={() =>
                  setIsTranslated((prevIsTranslated) => !prevIsTranslated)
                }
              >
                {isTranslated ? en.seeOriginal : en.seeTranslation}
              </div>
            )}
          </div>
        </div>
      )}

      {showViewDetails && (
        <button
          className={styles.viewDetails}
          onClick={(event) => {
            event.stopPropagation()
            onViewDetails?.()
          }}
          type="button"
        >
          {!!commentsCount && (
            <span className={styles.commentsCount}>
              <CommentsIcon className={styles.commentsIcon} />
              {numberWithCommas(commentsCount)}
            </span>
          )}
          View Details
          <span className={styles.viewDetailsChevron}>›</span>
        </button>
      )}
    </div>
  )
}
